import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BASE_URL from "Utilities/BASE_URL";

const SERVER_URL = BASE_URL.replace(/\/api$/, "");

// Backend may return profileImage (string), profileImage.data (object), or image.
const resolveImg = (data) => {
  let p = null;
  if (typeof data?.profileImage === "string" && data.profileImage) p = data.profileImage;
  else if (data?.profileImage?.data) p = data.profileImage.data;
  else if (typeof data?.image === "string" && data.image) p = data.image;
  else if (data?.image?.data) p = data.image.data;
  if (!p) return null;
  return p.startsWith("http") ? p : `${SERVER_URL}/${p}`;
};

// One round avatar (image or initial fallback). Overlapping avatars in a stack
// get a dark border so they read as separate discs.
const Avatar = ({ inst, size, overlap }) => {
  const img = resolveImg(inst);
  const style = { width: size, height: size, ...(overlap ? { marginInlineStart: -9 } : null) };
  const ring = overlap ? "border-2 border-black/60" : "ring-1 ring-white/20";
  return img ? (
    <img
      src={img}
      alt={inst?.name}
      style={style}
      className={`rounded-full object-cover ${ring}`}
    />
  ) : (
    <div
      style={style}
      className={`rounded-full bg-primary/30 flex items-center justify-center text-xs font-bold text-white ${ring}`}
    >
      {inst?.name?.charAt(0)}
    </div>
  );
};

// Watch-page instructor — compact. A role label + round avatar(s) + name(s),
// no bio (that lives on the profile page). Accepts a single instructor or list.
const InstructorCard = ({ data }) => {
  const { t } = useTranslation();
  const list = Array.isArray(data) ? data.filter(Boolean) : data ? [data] : [];
  if (list.length === 0) return null;
  const single = list.length === 1;

  return (
    <div>
      <p className="text-primary text-[10px] uppercase tracking-[0.13em] font-semibold mb-2">
        {single
          ? t("courses.instructor") || "Instructor"
          : t("courses.instructors") || "Instructors"}
      </p>

      {single ? (
        <div className="flex items-center gap-3">
          <Avatar inst={list[0]} size={40} />
          <div className="min-w-0">
            <Link
              to={`/instructor-profile/${list[0]?.slug}`}
              className="block truncate text-white font-bold text-[15px] hover:text-primary transition-colors"
            >
              {list[0]?.name}
            </Link>
            <Link
              to={`/instructor-profile/${list[0]?.slug}`}
              className="text-primary text-xs font-semibold hover:underline"
            >
              {t("courses.view_profile")}
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="flex shrink-0">
            {list.map((inst, i) => (
              <Avatar key={inst._id || i} inst={inst} size={30} overlap={i > 0} />
            ))}
          </div>
          <p className="text-white text-sm leading-snug">
            {list.map((inst, i) => (
              <React.Fragment key={inst._id || i}>
                <Link
                  to={`/instructor-profile/${inst?.slug}`}
                  className="font-semibold hover:text-primary transition-colors"
                >
                  {inst?.name}
                </Link>
                {i < list.length - 1 && <span className="text-white/50">, </span>}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}
    </div>
  );
};

export default InstructorCard;
